import { useCallback, useMemo, useState } from 'react';
import cn from 'classnames';
import { useField } from 'react-final-form';

import { IBooleanSelectOption } from 'KnmPackage/MainModule/interfaces/IBooleanSelectOption';
import { IKnmErknmFormViolation } from 'KnmErknmPackage/ErknmViolationsModule/interfaces/IKnmErknmFormViolation';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { booleanSelectOptions } from 'KnmPackage/MainModule/constants/booleanSelectOptions';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { ViolationStatus } from 'KnmErknmPackage/ErknmViolationsModule/components/ViolationStatus';
import { IconQuestion } from 'UIPackage/IconsModule/components/IconQuestion';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { NonClosableTooltip } from 'UIPackage/MainModule/components/NonClosableTooltip';
import { ReactQuillEditorField } from 'UIPackage/MainModule/components/ReactQuillEditorField';
import { ViolationStatusesEnum } from 'APIPackage/KnmModule/enums/ViolationStatusesEnum';
import { AgreementStatusEnum } from 'APIPackage/KnmModule/enums/AgreementStatusEnum';

import { KnmCollapse } from '../../../KnmPackage/MainModule/components/KnmCollapse';
import styles from './ErknmViolationItem.module.scss';
import { convertQuillDeltaToHtml } from '../../../functions/convertQuillDeltaToHtml';
import { IReactQuillDelta } from '../../../APIPackage/KnmModule/interfaces/IReactQuillDelta';
import { checkHasErrorInCollapseBlock } from '../../../functions/checkHasErrorInCollapseBlock';
import { ErknmDecisionViolationFixItems } from '../../../DecisionPackage/DecisionViolationModule/components/ErknmDecisionViolationFixItems';

interface IErknmActRepeatViolationItemProps {
  violation: IKnmErknmFormViolation;
  fieldName: string;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  isPaperless: boolean;
  id: string;
  currentErpId?: string;
  isKnm?: boolean;
  isPm?: boolean;
  isDecision?: boolean;
  isDisabledByDirty?: boolean;
  watchOnly: boolean;
  resigningRequired?: boolean;
  isActFirstSigned?: boolean;
  hasUnsignedAct?: boolean;
}

const negativeSelectOption = booleanSelectOptions.find(option => !option.value);

export const ErknmActRepeatViolationItem = ({
  violation,
  fieldName,
  formRef,
  isPaperless,
  id,
  currentErpId,
  isKnm,
  isPm,
  isDecision,
  isDisabledByDirty,
  watchOnly,
  resigningRequired,
  isActFirstSigned = false,
  hasUnsignedAct = false,
}: IErknmActRepeatViolationItemProps) => {
  const [isKnmCollapseOpened, setIsKnmCollapseOpened] = useState<boolean>(true);
  // @ts-ignore
  const erpId = formRef?.current?.getFieldState(isDecision ? `reasons[0].links` : `reasonsErknm[0].links`)?.value?.[0]
    ?.erpId;

  const isNotFixedByStatus: boolean =
    violation.status === ViolationStatusesEnum.NOT_FIXED || violation.status === ViolationStatusesEnum.APPROVING;

  const agreements: IKnmForm['agreements'] = useField('agreements' as keyof IKnmForm, { subscription: { value: true } })
    .input.value;

  const repeatCorrectedValue: IBooleanSelectOption = useField(`${fieldName}.repeatCorrected`, {
    subscription: { value: true },
  })?.input?.value;

  const repeatCorrectedInEventValue: IBooleanSelectOption = useField(`${fieldName}.repeatCorrectedInEvent`, {
    subscription: { value: true },
  })?.input?.value;

  const onChangeIsFixedOnPlace = useCallback(
    (v: IBooleanSelectOption) => {
      if (!v.value && violation.explanation) {
        // @ts-ignore
        formRef?.current?.change(`${fieldName}.explanation` as keyof IKnmForm);
      }
      if (!v.value && violation.repeatExplanation) {
        // @ts-ignore
        formRef?.current?.change(`${fieldName}.repeatExplanation` as keyof IKnmForm);
      }
      if (v.value === true) {
        // @ts-ignore
        formRef?.current.change(`${fieldName}.includeInSubject`, false);
      }
    },
    [violation.explanation],
  );

  const handleIsFixedChange = useCallback(
    (v: IBooleanSelectOption) => {
      if (isPaperless) {
        // @ts-ignore
        formRef?.current.change(`${fieldName}.isViolationResolved`, v);
        if (v.value === false) {
          // @ts-ignore
          formRef?.current.change(`${fieldName}.fixes`);
        }
        if (v.value === true) {
          // @ts-ignore
          formRef?.current.change(`${fieldName}.includeInSubject`, false);
        }
      }
    },
    [formRef, isPaperless],
  );

  /**
   * Определение занятости нарушения в соглашениях
   */
  const isDisabledByAgreement = useMemo(() => {
    if (!Array.isArray(agreements)) {
      return false;
    }

    const availableAgreements = agreements?.filter(
      agreement =>
        agreement.status === AgreementStatusEnum.DRAFT ||
        agreement.status === AgreementStatusEnum.READY_TO_APPROVE ||
        agreement.status === AgreementStatusEnum.ON_APPROVAL ||
        agreement.status === AgreementStatusEnum.APPROVED ||
        agreement.status === AgreementStatusEnum.PENDING_TERMINATION ||
        agreement.status === AgreementStatusEnum.COMPLETED,
    );

    if (!availableAgreements || availableAgreements.length === 0) {
      return false;
    }

    let isDisabled = false;

    availableAgreements.forEach(agreement => {
      agreement.violations?.forEach(agrViolation => {
        agrViolation.violatedMandatory.forEach(vm => {
          vm.violations?.forEach(vi => {
            if (vi.id === violation.id) {
              isDisabled = true;
            }
          });
        });
      });
    });

    return isDisabled;
  }, [agreements, violation]);

  const hasError = checkHasErrorInCollapseBlock(formRef, fieldName);

  return (
    <KnmCollapse
      id={id}
      title={
        <span className={`${styles.ItemCollapseTitle} shared-break-word`}>
          <span>Нарушение №{violation.number}:</span>{' '}
          {violation.descriptionReactQuillEditor && typeof violation.descriptionReactQuillEditor === 'object' ? (
            <div
              /* eslint-disable-next-line react/no-danger */
              dangerouslySetInnerHTML={{
                __html: convertQuillDeltaToHtml(violation.descriptionReactQuillEditor as IReactQuillDelta),
              }}
              className="ql-editor ql-editor-preview-block"
              style={{ whiteSpace: 'pre-wrap' }}
            />
          ) : (
            <>«{violation.title}»</>
          )}
        </span>
      }
      bodyClassName={styles.ItemCollapseBody}
      headerPostfix={
        <div className={styles.PostfixWrapper}>
          <ViolationStatus status={violation.status} />
          {violation.status !== ViolationStatusesEnum.FIXED &&
            (repeatCorrectedValue.value || repeatCorrectedInEventValue.value) &&
            resigningRequired && (
              <span className={styles.StatusNotificationGreen}>
                После подписания акта статус нарушения изменится на &rdquo;Устранено&rdquo;
              </span>
            )}
          {violation.resolvedIn && violation.resolvedIn !== currentErpId && (
            <span className={styles.StatusNotificationYellow}>Нарушение устранено в другом мероприятии</span>
          )}
        </div>
      }
      error={isKnmCollapseOpened && hasError}
      changeOpenedCallback={setIsKnmCollapseOpened}
    >
      <>
        <Row>
          <FieldBlock
            label={
              <Row align="center" margin="none" notSpace>
                <span>
                  {getTitleFieldWithRedStar({
                    label: 'Нарушение устранено',
                    field: `${fieldName}.repeatCorrected`,
                    isPm,
                    isKnm,
                    isDecision,
                  })}
                </span>
                <NonClosableTooltip
                  content={
                    <div>
                      Предметом текущего мероприятия было нарушение, включенное в предписание по мероприятию №{' '}
                      <a href={violation.prescriptionLink} target="_blank" rel="noreferrer">
                        {erpId}
                      </a>
                      . Необходимо установить значение «Да» в поле, если необходимо подтвердить факт устранения
                      нарушения
                    </div>
                  }
                >
                  <IconQuestion className={styles.TooltipIcon} />
                </NonClosableTooltip>
              </Row>
            }
            placementLabel="left"
            color={isPaperless ? 'black' : undefined}
            className={cn({ [styles.FieldBlock]: isPaperless })}
            labelSize="col-3"
            labelFontSize="small"
            type="not-space"
            id={`${id}.repeatCorrected`}
          >
            <FieldBlock size={!isPaperless ? 'col-3' : undefined}>
              <SelectInputField
                className={isPaperless ? '' : 'shared-row-field'}
                name={`${fieldName}.repeatCorrected`}
                options={booleanSelectOptions}
                isClearable={false}
                defaultValue={negativeSelectOption}
                disabled={
                  !(isNotFixedByStatus || hasUnsignedAct) || watchOnly || isDisabledByDirty || isDisabledByAgreement
                }
                onChange={handleIsFixedChange}
              />
            </FieldBlock>
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock
            label="Нарушение устранено в ходе мероприятия"
            placementLabel="left"
            color={isPaperless ? 'black' : undefined}
            className={cn({ [styles.FieldBlock]: isPaperless })}
            labelSize="col-3"
            labelFontSize="small"
            type="not-space"
            id={`${id}.repeatCorrectedInEvent`}
          >
            <FieldBlock size={!isPaperless ? 'col-3' : undefined}>
              <SelectInputField
                className={isPaperless ? '' : 'shared-row-field'}
                name={`${fieldName}.repeatCorrectedInEvent`}
                options={booleanSelectOptions}
                isClearable={false}
                defaultValue={negativeSelectOption}
                onChange={onChangeIsFixedOnPlace}
                disabled={
                  !(isNotFixedByStatus || !isActFirstSigned) || watchOnly || isDisabledByDirty || isDisabledByAgreement
                }
              />
            </FieldBlock>
          </FieldBlock>
        </Row>
        {(violation.repeatCorrected?.value === false || violation.repeatCorrectedInEvent?.value) && (
          <Row>
            <FieldBlock
              label="Пояснение"
              placementLabel="left"
              labelSize="col-3"
              labelFontSize="small"
              type="not-space"
              className={cn(styles.FieldBlock, styles.GroupMarginBottom)}
              color="black"
            >
              <ReactQuillEditorField
                name={`${fieldName}.repeatExplanation`}
                placeholder="Введите пояснение (при необходимости)"
                handleCustomChange={(e: string) => {
                  // @ts-ignore
                  (isDecision ? decisionFormRef : formRef)?.current?.change(`${fieldName}.repeatExplanation`, e);
                }}
                disabled={
                  !(isNotFixedByStatus || hasUnsignedAct) || watchOnly || isDisabledByDirty || isDisabledByAgreement
                }
              />
            </FieldBlock>
          </Row>
        )}
        {violation.repeatCorrected?.value && (
          <ErknmDecisionViolationFixItems
            violation={violation}
            formRef={formRef}
            name={fieldName}
            isFixedFieldName="repeatCorrected"
            watchOnly={watchOnly}
            isPaperless={isPaperless}
            inAct
            isDisabledByDirty={isDisabledByDirty}
            isDisabledByAgreement={isDisabledByAgreement}
            hasUnsignedAct={hasUnsignedAct}
            isKnm={isKnm}
            isPm={isPm}
            isDecision={isDecision}
            id={id}
            isFixed={violation.repeatCorrected?.value}
            isRepeatCheck
          />
        )}
      </>
    </KnmCollapse>
  );
};
